import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import brandMark from '../assets/brand/appealstack-icon-white.svg';
import { roleLabel } from '../lib/roles';
import { api } from '../lib/api';
import { navPrefetchTarget } from '../lib/navPrefetch';

/**
 * Sidebar — left-side persistent navigation drawer (Standard variant).
 *
 * Derived from M3 Navigation Drawer (Standard persistent variant) per
 * Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/navigation-drawer/overview
 *   - Reference: github.com/material-components/material-web/tree/main/navigationdrawer
 *
 * Populus adaptations applied:
 *   - Dark theme surface (--color-sidebar #1E2A30) — matches Christophe's
 *     entity-detail screenshots. M3 default is light; Populus inverts for
 *     visual separation from the white content surface.
 *   - 240px fixed width on desktop; below the `lg` breakpoint (compact) it
 *     becomes a fixed off-canvas drawer that slides in over a backdrop,
 *     dismissed by the close button, a nav tap, or the backdrop (the
 *     open/backdrop state is owned by AppLayout).
 *   - Section labels in UPPERCASE Label Small (M3 Label Small spec).
 *   - Active item: 3px left-edge --brand-primary accent + subtle white
 *     overlay background (vs M3's filled pill — Populus prefers an edge
 *     accent that reads at a glance on dark surfaces).
 *   - AS logo + product label at top; user identity block at bottom
 *     (visible on every screen; matches Christophe's reference).
 *
 * Token discipline:
 *   - Surface → --color-sidebar (system; never per-org)
 *   - Active accent → --brand-primary (org-overridable per design-system.md
 *     § Color — System vs Brand tokens)
 *   - Text → hardcoded white + rgba(255,255,255,N) for hierarchy (sidebar
 *     dark theme is system-fixed; no semantic ink token applies on dark)
 *
 * Phase 11 Block 6 PR 4 (2026-05-12).
 */

export interface SidebarUser {
  name: string;
  email: string;
  role: string;
}

export interface SidebarSection {
  label?: string;
  items: SidebarItem[];
}

export interface SidebarItem {
  to: string;
  label: string;
  badge?: ReactNode;
  end?: boolean;
}

export interface SidebarProps {
  sections: SidebarSection[];
  user?: SidebarUser;
  brandName?: string;
  brandTagline?: string;
  /** Below `lg`: render as an off-canvas drawer instead of a persistent rail. */
  compact?: boolean;
  /** Drawer visibility (compact only). */
  open?: boolean;
  /** Dismiss the drawer (close button + nav tap). */
  onClose?: () => void;
}

export function Sidebar({
  sections,
  user,
  compact = false,
  open = false,
  onClose,
}: SidebarProps) {
  const queryClient = useQueryClient();

  // Hover/focus intent → warm the destination route's PRIMARY list query so the
  // data is already cached when the click lands (the backend pays a ~200ms
  // round-trip; this hides it). Best-effort: unknown routes no-op, and any fetch
  // error is swallowed (prefetchQuery never rejects). Respects the shared
  // staleTime — a still-fresh entry is not refetched.
  const prefetchRoute = (to: string) => {
    const target = navPrefetchTarget(to);
    if (!target) return;
    void queryClient.prefetchQuery({
      queryKey: target.queryKey,
      queryFn: ({ signal }) => api.get(target.url, signal),
    });
  };

  const base = {
    background: 'var(--color-sidebar)',
    color: '#fff',
    width: 240,
    display: 'flex',
    flexDirection: 'column',
  } as const;

  const frame = compact
    ? {
        ...base,
        position: 'fixed' as const,
        top: 0,
        left: 0,
        height: '100dvh',
        zIndex: 30,
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 200ms ease',
        boxShadow: open ? 'var(--sh-lg)' : 'none',
      }
    : {
        ...base,
        gridArea: 'sidebar',
        height: '100%',
        minHeight: 0,
      };

  return (
    <aside
      aria-hidden={compact && !open ? true : undefined}
      style={frame}
    >
      <header style={{
        padding: 'var(--sp-5) var(--sp-4) var(--sp-4)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
      }}>
        <img
          src={brandMark}
          alt=""
          aria-hidden="true"
          width={32}
          height={32}
          style={{ display: 'block', flexShrink: 0 }}
        />
        <div style={{
          minWidth: 0,
          fontSize: 'var(--fs-lg)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          lineHeight: 'var(--lh-tight)',
          color: '#fff',
        }}>appealstack</div>
        {compact && (
          <button
            type="button"
            onClick={() => onClose?.()}
            aria-label="Close navigation menu"
            style={{
              marginLeft: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              flexShrink: 0,
              border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: 'var(--r-md)',
              background: 'transparent',
              color: 'rgba(255,255,255,0.8)',
              cursor: 'pointer',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        )}
      </header>

      {/* data-tour: a STABLE hook for the welcome tour's spotlight. It is an
          explicit contract, not a CSS class the tour scrapes — a restyle must not
          silently leave the tour highlighting nothing. */}
      <nav data-tour="sidebar" className="sidebar-nav" style={{
        flex: 1,
        overflowY: 'auto',
        padding: 'var(--sp-3) 0',
      }}>
        {sections.map((section, i) => (
          // Stable identity key (section label, else the first item's route) — NOT
          // the array index. When permission gating adds/removes a section, an
          // index key makes React reuse a DOM node for different section content,
          // so an in-flight click can land on a stale NavLink handler. A stable key
          // reconciles each section to its own node.
          <div key={section.label ?? section.items[0]?.to ?? `section-${i}`} style={{ marginBottom: 'var(--sp-4)' }}>
            {section.label && (
              <div style={{
                padding: '0 var(--sp-4) var(--sp-2)',
                fontSize: 'var(--fs-xs)',
                fontWeight: 'var(--fw-semibold)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--ls-wider)',
                // 0.55 white over the sidebar is #9A9FA2, 5.5:1 (AA); 0.4 was 3.6:1.
                color: 'rgba(255,255,255,0.55)',
              }}>{section.label}</div>
            )}
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {section.items.map(item => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    // Prefetch the destination's list data on hover/focus intent
                    // (mouse users on enter; keyboard users on focus), so the
                    // click resolves against a warm cache instead of a cold
                    // ~200ms fetch. No-op for routes absent from the registry.
                    onMouseEnter={() => prefetchRoute(item.to)}
                    onFocus={() => prefetchRoute(item.to)}
                    // Defer the drawer close one frame so the tap's navigation
                    // fully commits on the still-mounted drawer before it starts
                    // sliding out — a synchronous close can drop the first tap on
                    // touch, which reads as a "double-tap to navigate" bug. The
                    // AppLayout pathname effect also closes on a route change, so
                    // this rAF only owns the same-route tap.
                    onClick={() => { if (compact && onClose) requestAnimationFrame(onClose); }}
                    style={({ isActive }) => ({
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 'var(--sp-2)',
                      // M3 touch target: a comfortable ≥44px nav row on desktop
                      // (coarse-pointer floor in global.css lifts it to 48px on touch).
                      minHeight: 44,
                      boxSizing: 'border-box',
                      padding: 'var(--sp-2) var(--sp-4)',
                      paddingLeft: isActive ? `calc(var(--sp-4) - 3px)` : 'var(--sp-4)',
                      borderLeft: isActive ? '3px solid var(--brand-primary)' : '3px solid transparent',
                      color: isActive ? '#fff' : 'rgba(255,255,255,0.7)',
                      background: isActive ? 'rgba(255,255,255,0.04)' : 'transparent',
                      textDecoration: 'none',
                      fontSize: 'var(--fs-md)',
                      fontWeight: isActive ? 'var(--fw-medium)' : 'var(--fw-regular)',
                      transition: 'background-color 120ms ease, color 120ms ease',
                    })}
                  >
                    <span>{item.label}</span>
                    {item.badge !== undefined && (
                      <span style={{
                        fontSize: 'var(--fs-xs)',
                        color: 'rgba(255,255,255,0.5)',
                      }}>{item.badge}</span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {user && (
        <footer style={{
          padding: 'var(--sp-3) var(--sp-4)',
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 'var(--sp-2)',
          }}>
            <div style={{
              width: 32, height: 32,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
              display: 'grid', placeItems: 'center',
              fontSize: 'var(--fs-sm)',
              fontWeight: 'var(--fw-semibold)',
            }}>{initials(user.name)}</div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: 'var(--fs-sm)',
                fontWeight: 'var(--fw-medium)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>{user.name}</div>
              <div style={{
                fontSize: 'var(--fs-xs)',
                color: 'rgba(255,255,255,0.5)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--ls-wider)',
              }}>{roleLabel(user.role)}</div>
            </div>
          </div>
        </footer>
      )}
    </aside>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map(part => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
